import { Link, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { UserDTO } from "../types/userDTO";
import { StationDTO } from "../types/stationDTO";
import { useFetchUsers } from "../hooks/UserHooks";
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from "react";
import { useFetchStations } from "../hooks/StationHooks";

type UserListArgs = {
    selectedStationId: string;
    selectedStationIdChanged: (selectedStationId: string) => void;
};

const UserList = ({ selectedStationId, selectedStationIdChanged }: UserListArgs ) => {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { organisationId } = useParams<{ organisationId: string }>();
  if (!organisationId) throw new Error("Missing organisationId from params");
  const { data, isPending, isError } = useFetchUsers(organisationId);
  const [sortField, setSortField] = useState<keyof UserDTO>('userName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [stations, setStations] = useState<StationDTO[]>([]);
  const { data: stationsData, isLoading: stationsIsLoading, isError: stationsIsError } = useFetchStations(organisationId);


useEffect(() => {
    if (stationsData) {
        setStations(stationsData);

        if (selectedStationId != "all" && selectedStationId != "none") {
            const stationExists = stationsData.some(station => station.id === selectedStationId);
            if (!stationExists) {
                selectedStationIdChanged("all"); // Reset to "all" if the selected station is not found
            }
        }
    }
}, [stationsData]);

useEffect(() => {
    if (isError || stationsIsError) { nav('/login') }

}, [isError, stationsIsError]);


  const handleSort = (field: keyof UserDTO) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortData = (data: UserDTO[]) => {
    return [...data].sort((a, b) => {
      if (sortField === 'userName') {
        return sortDirection === 'asc'
          ? a.userName.localeCompare(b.userName)
          : b.userName.localeCompare(a.userName);
        }

        if (sortField === 'stations') {
            return sortDirection === 'asc'
                ? a.stations.map(c => c.name).join(', ').localeCompare(b.stations.map(c => c.name).join(', '))
                : b.stations.map(c => c.name).join(', ').localeCompare(a.stations.map(c => c.name).join(', '));
        }

      if (sortField === 'firstName') {
        return sortDirection === 'asc'
          ? a.firstName.localeCompare(b.firstName)
          : b.firstName.localeCompare(a.firstName);
      }

      if (sortField === 'lastName') {
        return sortDirection === 'asc'
          ? a.lastName.localeCompare(b.lastName)
          : b.lastName.localeCompare(a.lastName);
      }

      if (sortField === 'capabilities') {
        return sortDirection === 'asc'
          ? a.capabilities.map(c => c.name).join(', ').localeCompare(b.capabilities.map(c => c.name).join(', '))
          : b.capabilities.map(c => c.name).join(', ').localeCompare(a.capabilities.map(c => c.name).join(', '));
        }

        if (sortField === 'userGroups') {
            return sortDirection === 'asc'
                ? a.userGroups.map(c => c.name).join(', ').localeCompare(b.userGroups.map(c => c.name).join(', '))
                : b.userGroups.map(c => c.name).join(', ').localeCompare(a.userGroups.map(c => c.name).join(', '));
        }

      if (sortField === 'validFrom' || sortField === 'validTo' || sortField === 'lastModified') {
        const aValue = new Date(a[sortField]).getTime();
        const bValue = new Date(b[sortField]).getTime();
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  };

    const filteredData = data?.filter(user => {
        const matchesSearch = user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.capabilities.map(c => c.name).join(', ').toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.userGroups.map(g => g.name).join(', ').toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.stations.map(c => c.name).join(', ').toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStation = selectedStationId === "all" ||
            (selectedStationId === "none" && user.stations.length === 0) ||
            user.stations.some(station => station.id === selectedStationId);

        return matchesSearch && matchesStation;
    });

  

  if (isPending || stationsIsLoading) return <h2>Hold on</h2>;

  return (
    <div>
      <div className="row mb-2 mt-2">
        <h5 className="subtitle text-left">
          {t('user.title')}
        </h5>
      </div>

      <Link className="btn btn-primary mt-2 mb-3" to={`/organisation/${organisationId}/user/add`}>
        {t('user.add')}
          </Link>

        <Link className="btn btn-primary mt-2 mb-3 ms-3" to={`/organisation/${organisationId}/user/storeimport`}>
            {t('user.storeImport')}
        </Link>

    <div className="row mb-3">
        <div className="col-md-4">
            <input
                type="text"
                className="form-control"
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
        </div>
        <div className="col-md-4">
            <select
                className="form-select"
                value={selectedStationId}
                onChange={(e) => {
                    selectedStationIdChanged(e.target.value);
                }}
            >
                <option value="all">{t('common.fromAllStations')}</option>
                <option value="none">{t('common.atNoStation')}</option>
                {stations?.map((station) => (
                    <option key={station.id} value={station.id}>
                        {station.name}
                    </option>
                ))}
            </select>
        </div>
    </div>


    <table
        className="table table-hover table-striped"
        style={
            {
                "--bs-table-hover-bg": "#C9DDED"
            } as React.CSSProperties
        }
    >
      <thead>
          <tr>
            <th onClick={() => handleSort('userName')} style={{ cursor: 'pointer' }}>
              {t('common.username')} {sortField === 'userName' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('firstName')} style={{ cursor: 'pointer' }}>
              {t('user.firstName')} {sortField === 'firstName' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('lastName')} style={{ cursor: 'pointer' }}>
              {t('user.lastName')} {sortField === 'lastName' && (sortDirection === 'asc' ? '▲' : '▼')}
             </th>
            <th onClick={() => handleSort('stations')} style={{ cursor: 'pointer' }}>
                {t('user.stations')} {sortField === 'stations' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('capabilities')} style={{ cursor: 'pointer' }}>
              {t('user.capabilities')} {sortField === 'capabilities' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('userGroups')} style={{ cursor: 'pointer' }}>
                {t('user.groups')} {sortField === 'userGroups' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('validFrom')} style={{ cursor: 'pointer' }}>
              {t('common.validFrom')} {sortField === 'validFrom' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('validTo')} style={{ cursor: 'pointer' }}>
              {t('common.validTo')} {sortField === 'validTo' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('lastModified')} style={{ cursor: 'pointer' }}>
              {t('common.lastModified')} {sortField === 'lastModified' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredData && sortData(filteredData).map((user: UserDTO) => {
              const dateOptions: Intl.DateTimeFormatOptions = {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              };

              return (
                <tr
                  key={user.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => nav(`/organisation/${organisationId}/user/${user.id}`)}
                  className={user.isLocked ? 'table-danger' : ''}>
                  <td>{user.userName} {user.isLocked ? t('user.isLocked') : ''}</td>
                  <td>{user.firstName}</td>
                      <td>{user.lastName}</td>
                      <td>{user.stations.map(s => s.name).join(', ')}</td>
                  {/* <td>{u.roles.map(r => r).join(', ')}</td> */}
                    <td>{user.capabilities.map(c => c.name).join(', ')}</td>
                    <td>{user.userGroups.map(g => g.name).join(', ')}</td>
                  <td>{new Date(user.validFrom + 'Z').toLocaleString(i18n.language === 'fi' ? 'fi-FI' : 'en-US', dateOptions)}</td>
                  <td>{new Date(user.validTo + 'Z').toLocaleString(i18n.language === 'fi' ? 'fi-FI' : 'en-US', dateOptions)}</td>
                  <td>{new Date(user.lastModified + 'Z').toLocaleString(i18n.language === 'fi' ? 'fi-FI' : 'en-US', dateOptions)}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
};

export default UserList;
